import logger from './controllers/logger';

import { DeviceSelection, type GameManager } from './types';

let wakeLock: WakeLockSentinel = null;
let deviceSelection: DeviceSelection = {
  cameraLabel: '',
  micLabel: '',
  speakerLabel: '',
  cameraId: '',
  micId: '',
  speakerId: '',
};
let backgroundEffect: 'none' | 'slight-blur' | 'blur' = 'none';

export let audioMuted: boolean;
export let videoMuted: boolean;

export function init(manager: GameManager) {
  const defaultsJson = localStorage.getItem('defaults');
  if (defaultsJson) {
    try {
      const defaults = JSON.parse(defaultsJson);
      if (defaults.devices) {
        deviceSelection = {
          ...deviceSelection,
          ...defaults.devices,
        };
      }
      if (defaults.backgroundEffect) {
        if (
          defaults.backgroundEffect === 'none' ||
          defaults.backgroundEffect === 'slight-blur' ||
          defaults.backgroundEffect === 'blur'
        )
          backgroundEffect = defaults.backgroundEffect;
      }
    } catch {
      localStorage.removeItem('defaults');
    }
  }

  navigator.mediaDevices.enumerateDevices().then((devices) => {
    const camLabel = deviceSelection.cameraLabel.toLowerCase();
    const micLabel = deviceSelection.micLabel.toLowerCase();
    for (let i = devices.length - 1; i >= 0; i--) {
      const device = devices[i];
      if (camLabel) {
        const camDeviceIndex = device.label.toLowerCase().indexOf(camLabel);
        if (camDeviceIndex >= 0) {
          if (device.kind === 'videoinput') {
            if (!deviceSelection.cameraId || device.deviceId === 'default') {
              deviceSelection.cameraId = device.deviceId;
              deviceSelection.cameraLabel = device.label;
            }
          }
        }
      }
      if (micLabel) {
        const micDeviceIndex = device.label.toLowerCase().indexOf(micLabel);
        if (micDeviceIndex >= 0) {
          if (device.kind === 'audioinput') {
            if (!deviceSelection.micId || device.deviceId === 'default') {
              deviceSelection.micId = device.deviceId;
              deviceSelection.micLabel = device.label;
            }
          }
        }
      }
    }

    if (deviceSelection.cameraLabel) {
      const label = deviceSelection.cameraLabel;
    }

    if (deviceSelection.speakerLabel) {
      const foundDefaultSpeaker = devices.find(
        (it) =>
          it.kind === 'audiooutput' && it.label === 'Default - ' + deviceSelection.speakerLabel,
      );

      if (foundDefaultSpeaker) {
        deviceSelection.speakerId = 'default';
        deviceSelection.speakerLabel = foundDefaultSpeaker.label;
      } else {
        const foundSpeaker = devices.find(
          (it) => it.kind === 'audiooutput' && it.label === deviceSelection.speakerLabel,
        );
        if (foundSpeaker) {
          deviceSelection.speakerId = foundSpeaker.deviceId;
          deviceSelection.speakerLabel = foundSpeaker.label;
        } else {
          //speaker device is not found, just use the default;
          deviceSelection.speakerId = 'default';
          deviceSelection.speakerLabel = '';
        }
      }
    } else {
      const foundDefaultSpeaker = devices.find(
        (it) =>
          it.kind === 'audiooutput' && it.label === 'Default - ' + deviceSelection.speakerLabel,
      );

      if (foundDefaultSpeaker) {
        deviceSelection.speakerId = 'default';
        deviceSelection.speakerLabel = foundDefaultSpeaker.label;
      }
    }

  });
}

export function saveDeviceSelection(_deviceSelection: Partial<DeviceSelection>) {
  const defaultsJson = localStorage.getItem('defaults');

  const defaults = defaultsJson
    ? JSON.parse(defaultsJson)
    : {
        devices: {},
      };

  deviceSelection = { ...deviceSelection, ..._deviceSelection };
  defaults.devices.cameraLabel = deviceSelection.cameraLabel;
  defaults.devices.micLabel = deviceSelection.micLabel;
  defaults.devices.speakerLabel = deviceSelection.speakerLabel;

  localStorage.setItem('defaults', JSON.stringify(defaults));
}

export function getDeviceSelection(): DeviceSelection {
  return deviceSelection;
}

export function saveBackgroundEffect(effect: 'none' | 'slight-blur' | 'blur') {
  if (backgroundEffect === effect) return;

  backgroundEffect = effect;

  const defaultsJson = localStorage.getItem('defaults');
  const defaults = JSON.parse(defaultsJson);

  defaults.backgroundEffect = backgroundEffect;

  localStorage.setItem('defaults', JSON.stringify(defaults));
}

export function getSavedBackgroundEffect(): 'none' | 'slight-blur' | 'blur' {
  return backgroundEffect;
}

export function setAudioMuted(isMuted: boolean) {
  audioMuted = isMuted;
}

export function setVideoMuted(isMuted: boolean) {
  videoMuted = isMuted;
}

export async function startWakeLock() {
  // create an async function to request a wake lock
  try {
    wakeLock = await navigator.wakeLock.request('screen');
    logger.debug('Wake Lock is active!');
  } catch (err) {
    // The Wake Lock request has failed - usually system related, such as battery.
    logger.warn('Wake Lock request failed', err);
  }
}

export function endWakeLock() {
  wakeLock = null;
}
